/**
 * v2 Settings → VPN (admin only).
 *
 * Configure the OpenVPN client that lets the stack reach off-net devices.
 * Username/password are write-only — the API returns only "is set"
 * booleans, never the values. Saving renders the config for the sidecar;
 * Connect/Disconnect toggle the tunnel. Status is polled for the badge
 * (also shown in the navbar).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Network, Plug, PlugZap, Save } from "lucide-react";
import { useEffect, useState } from "react";

import { api, asApiError } from "../../../api/client";
import { LocalTime } from "../../../lib/datetime";
import { Badge, type BadgeTone } from "../../components/Badge";
import { useToast } from "../../components/Toast";
import { HelpButton } from "../../help/HelpPanel";

type VpnConfig = {
  enabled: boolean;
  server_address: string | null;
  server_port: number;
  protocol: string;
  verify_cert: boolean;
  server_verify: ServerVerify;
  offnet_subnets: string[];
  has_username_set: boolean;
  has_password_set: boolean;
  has_extra_options: boolean;
  updated_by_gigafy_username: string | null;
  updated_at: string | null;
};
type ServerVerify = "custom" | "letsencrypt";

type VpnStatus = {
  state: string;
  since: string | null;
  server: string | null;
  detail: string | null;
};

const STATE_TONE: Record<string, BadgeTone> = {
  up: "success",
  connecting: "warning",
  down: "muted",
  error: "danger",
  unknown: "neutral",
};

const inputCls =
  "rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100 placeholder:text-slate-600 focus-visible:border-emerald-700 focus-visible:outline-none";

export function Vpn() {
  const qc = useQueryClient();
  const toast = useToast();

  const cfgQ = useQuery<VpnConfig>({
    queryKey: ["v2", "vpn", "config"],
    queryFn: async () => (await api.get<VpnConfig>("/vpn/config")).data,
    retry: false,
  });
  const statusQ = useQuery<VpnStatus>({
    queryKey: ["v2", "vpn", "status"],
    queryFn: async () => (await api.get<VpnStatus>("/vpn/status")).data,
    refetchInterval: 10_000,
    retry: false,
  });

  // Local form state, seeded from the loaded config.
  const [server, setServer] = useState("");
  const [port, setPort] = useState("1194");
  const [protocol, setProtocol] = useState("udp");
  const [verifyCert, setVerifyCert] = useState(true);
  const [serverVerify, setServerVerify] = useState<ServerVerify>("custom");
  const [subnets, setSubnets] = useState("");
  const [extra, setExtra] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    const c = cfgQ.data;
    if (!c) return;
    setServer(c.server_address ?? "");
    setPort(String(c.server_port ?? 1194));
    setProtocol(c.protocol ?? "udp");
    setVerifyCert(c.verify_cert);
    setServerVerify(c.server_verify ?? "custom");
    setSubnets((c.offnet_subnets ?? []).join("\n"));
    setExtra(""); // never echoed; left blank, only sent when changed
  }, [cfgQ.data]);

  const forbidden = asApiError(cfgQ.error).status === 403;

  const save = useMutation({
    mutationFn: async () => {
      const offnet = subnets
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const body: Record<string, unknown> = {
        server_address: server.trim() || null,
        server_port: parseInt(port, 10) || 1194,
        protocol,
        verify_cert: verifyCert,
        server_verify: serverVerify,
        offnet_subnets: offnet,
      };
      if (username) body.username = username;
      if (password) body.password = password;
      if (extra.trim()) body.extra_options = extra;
      return (await api.put<VpnConfig>("/vpn/config", body)).data;
    },
    onSuccess: () => {
      setUsername("");
      setPassword("");
      qc.invalidateQueries({ queryKey: ["v2", "vpn"] });
      toast.push("VPN config saved", "success");
    },
    onError: (err) =>
      toast.push(asApiError(err).detail || "Save failed", "error"),
  });

  const toggle = useMutation({
    mutationFn: async (connect: boolean) =>
      api.post(`/vpn/${connect ? "connect" : "disconnect"}`),
    onSuccess: (_d, connect) => {
      qc.invalidateQueries({ queryKey: ["v2", "vpn"] });
      toast.push(connect ? "Connecting…" : "Disconnecting…", "info");
    },
    onError: (err) =>
      toast.push(asApiError(err).detail || "Action failed", "error"),
  });

  const status = statusQ.data?.state ?? "unknown";
  const statusDetail = status === "error" ? statusQ.data?.detail : null;
  const enabled = cfgQ.data?.enabled ?? false;

  if (forbidden) {
    return (
      <section className="flex flex-col gap-3">
        <h1 className="text-lg font-semibold text-slate-100">VPN</h1>
        <p className="rounded border border-amber-800 bg-amber-900/30 p-3 text-xs text-amber-200">
          VPN configuration is admin-only. Ask an admin (a user in{" "}
          <code className="font-mono">CHASSIS_API_KEY_ADMINS</code>) to manage
          it.
        </p>
      </section>
    );
  }

  return (
    <section className="flex max-w-3xl flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h1 className="inline-flex items-center gap-2 text-lg font-semibold text-slate-100">
            <Network className="h-4 w-4" />
            VPN (off-net access)
          </h1>
          <HelpButton topic="settings-vpn" />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Tunnel:</span>
          <Badge tone={STATE_TONE[status] ?? "neutral"}>{status}</Badge>
          {enabled ? (
            <button
              type="button"
              onClick={() => toggle.mutate(false)}
              disabled={toggle.isPending}
              className="inline-flex items-center gap-1 rounded border border-red-800 bg-red-900/30 px-2 py-1 text-red-200 hover:bg-red-900/50 disabled:opacity-50"
            >
              <PlugZap className="h-3 w-3" /> Disconnect
            </button>
          ) : (
            <button
              type="button"
              onClick={() => toggle.mutate(true)}
              disabled={toggle.isPending || !cfgQ.data?.server_address}
              className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-2 py-1 text-emerald-100 hover:bg-emerald-900/70 disabled:opacity-50"
            >
              <Plug className="h-3 w-3" /> Connect
            </button>
          )}
        </div>
      </header>

      {statusDetail && (
        <p className="rounded border border-red-800 bg-red-900/30 p-3 text-xs text-red-200">
          Tunnel error: {statusDetail}
        </p>
      )}

      <p className="text-xs text-slate-500">
        Split-tunnel: only the off-net subnets below route through the VPN;
        all other traffic is unaffected. Credentials are write-only — they're
        stored encrypted and never shown again.
      </p>

      <div className="grid gap-3 rounded-lg bg-slate-900 shadow-border p-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">Server address</span>
          <input
            className={inputCls}
            value={server}
            onChange={(e) => setServer(e.target.value)}
            placeholder="vpn.example.com"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-slate-400">Port</span>
            <input
              className={inputCls}
              value={port}
              onChange={(e) => setPort(e.target.value)}
              inputMode="numeric"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-slate-400">Protocol</span>
            <select
              className={inputCls}
              value={protocol}
              onChange={(e) => setProtocol(e.target.value)}
            >
              <option value="udp">udp</option>
              <option value="tcp">tcp</option>
            </select>
          </label>
        </div>

        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">
            Username{" "}
            {cfgQ.data?.has_username_set && (
              <span className="text-emerald-400">(set — leave blank to keep)</span>
            )}
          </span>
          <input
            className={inputCls}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
            placeholder={cfgQ.data?.has_username_set ? "••••••••" : ""}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-slate-400">
            Password{" "}
            {cfgQ.data?.has_password_set && (
              <span className="text-emerald-400">(set — leave blank to keep)</span>
            )}
          </span>
          <input
            className={inputCls}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            placeholder={cfgQ.data?.has_password_set ? "••••••••" : ""}
          />
        </label>

        <fieldset className="flex flex-col gap-1.5 text-xs sm:col-span-2">
          <legend className="mb-1 text-slate-400">Server verification</legend>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="server-verify"
              checked={serverVerify === "letsencrypt"}
              onChange={() => setServerVerify("letsencrypt")}
              className="mt-0.5 h-3.5 w-3.5"
            />
            <span className="text-slate-300">
              Let&apos;s Encrypt CA + hostname{" "}
              <span className="text-emerald-400">(recommended)</span>
              <span className="block text-slate-500">
                Trusts the Let&apos;s Encrypt roots and checks the certificate
                is issued for the server address above, so the server&apos;s
                certificate renewals don&apos;t break the tunnel. Any
                fingerprint or CA lines in the extra directives are ignored.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="server-verify"
              checked={serverVerify === "custom"}
              onChange={() => setServerVerify("custom")}
              className="mt-0.5 h-3.5 w-3.5"
            />
            <span className="text-slate-300">
              Custom
              <span className="block text-slate-500">
                Use the extra directives below (e.g. your provider&apos;s{" "}
                <code className="font-mono">&lt;ca&gt;</code> or a{" "}
                <code className="font-mono">peer-fingerprint</code> pin —
                a pin must be updated every time the server renews its
                certificate).
              </span>
            </span>
          </label>
          {serverVerify === "custom" && (
            <label className="ml-5 flex items-center gap-2">
              <input
                type="checkbox"
                checked={verifyCert}
                onChange={(e) => setVerifyCert(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              <span className="text-slate-300">
                Verify server certificate (uncheck to ignore the SSL/TLS cert)
              </span>
            </label>
          )}
        </fieldset>

        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="text-slate-400">
            Off-net subnets (one per line, e.g. 192.168.50.0/24)
          </span>
          <textarea
            className={`${inputCls} h-20 font-mono`}
            value={subnets}
            onChange={(e) => setSubnets(e.target.value)}
            spellCheck={false}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs sm:col-span-2">
          <span className="text-slate-400">
            Extra OpenVPN directives (optional — e.g. inline{" "}
            <code className="font-mono">&lt;ca&gt;…&lt;/ca&gt;</code> /{" "}
            <code className="font-mono">tls-auth</code> from your provider)
            {cfgQ.data?.has_extra_options && (
              <span className="ml-1 text-emerald-400">
                (set — leave blank to keep)
              </span>
            )}
          </span>
          <textarea
            className={`${inputCls} h-24 font-mono`}
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            spellCheck={false}
            placeholder={cfgQ.data?.has_extra_options ? "(unchanged)" : ""}
          />
        </label>

        <div className="sm:col-span-2">
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-3 py-1.5 text-xs text-emerald-100 hover:bg-emerald-900/70 disabled:opacity-50"
          >
            <Save className="h-3 w-3" />
            {save.isPending ? "Saving…" : "Save config"}
          </button>
          {cfgQ.data?.updated_at && (
            <span className="ml-3 text-[10px] text-slate-500">
              last edited by {cfgQ.data.updated_by_gigafy_username ?? "?"} at{" "}
              <LocalTime iso={cfgQ.data.updated_at} />
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
